import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Settings, Eye, EyeOff, GripVertical } from 'lucide-react';

const COLUMN_MENU_WIDTH = 256;
const COLUMN_MENU_OFFSET = 8;

/**
 * Standalone column settings menu (gear icon) with:
 *   - per-column visibility toggles
 *   - drag-to-reorder list of columns
 *   - "Reset to default" link
 *
 * Owns nothing persistent — the parent owns the `order` + `hidden` state and
 * decides whether to write to localStorage. The menu just emits onChange().
 *
 * Props:
 *   columns     [{ key: string, label: string, alwaysVisible?: boolean }]
 *               The canonical list. `alwaysVisible` columns can't be hidden
 *               (e.g. an Actions column that must stay).
 *   order       string[]  current column order (keys)
 *   hidden      string[]  current hidden column keys
 *   defaults    { order: string[], hidden: string[] }  used by the reset link
 *   onChange    (order, hidden) => void
 */
function ColumnSettingsMenu({ columns, order, hidden, defaults, onChange }) {
  const [open, setOpen] = useState(false);
  const [dragKey, setDragKey] = useState(null);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const popupRef = useRef(null);
  const listScrollRef = useRef(null);
  const dragScrollDirectionRef = useRef(0);
  const dragScrollRafRef = useRef(null);

  const stopDragAutoScroll = useCallback(() => {
    dragScrollDirectionRef.current = 0;
    if (dragScrollRafRef.current != null) {
      cancelAnimationFrame(dragScrollRafRef.current);
      dragScrollRafRef.current = null;
    }
  }, []);

  const ensureDragAutoScrollLoop = useCallback(() => {
    if (dragScrollRafRef.current != null) return;
    const tick = () => {
      const direction = dragScrollDirectionRef.current;
      const list = listScrollRef.current;
      if (!direction || !list) {
        dragScrollRafRef.current = null;
        return;
      }
      list.scrollTop += direction * 10;
      dragScrollRafRef.current = requestAnimationFrame(tick);
    };
    dragScrollRafRef.current = requestAnimationFrame(tick);
  }, []);

  const updateDragAutoScrollFromClientY = useCallback(
    (clientY) => {
      const list = listScrollRef.current;
      if (!list) return;
      const rect = list.getBoundingClientRect();
      const edge = 36;
      if (clientY < rect.top + edge) {
        dragScrollDirectionRef.current = -1;
        ensureDragAutoScrollLoop();
      } else if (clientY > rect.bottom - edge) {
        dragScrollDirectionRef.current = 1;
        ensureDragAutoScrollLoop();
      } else {
        stopDragAutoScroll();
      }
    },
    [ensureDragAutoScrollLoop, stopDragAutoScroll],
  );

  useEffect(() => () => stopDragAutoScroll(), [stopDragAutoScroll]);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const desiredLeft = rect.right - COLUMN_MENU_WIDTH;
    const maxLeft = Math.max(
      COLUMN_MENU_OFFSET,
      window.innerWidth - COLUMN_MENU_WIDTH - COLUMN_MENU_OFFSET,
    );
    const left = Math.min(Math.max(COLUMN_MENU_OFFSET, desiredLeft), maxLeft);
    setPosition({ top: rect.bottom + COLUMN_MENU_OFFSET, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      const trigger = triggerRef.current;
      const popup = popupRef.current;
      const target = e.target;
      if (trigger && trigger.contains(target)) return;
      if (popup && popup.contains(target)) return;
      stopDragAutoScroll();
      setOpen(false);
    };
    const onReposition = () => updatePosition();
    document.addEventListener('mousedown', handler);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updatePosition, stopDragAutoScroll]);

  const columnsByKey = new Map(columns.map((c) => [c.key, c]));
  const hiddenSet = new Set(hidden);

  const toggleVisibility = (key) => {
    const col = columnsByKey.get(key);
    if (!col || col.alwaysVisible) return;
    const next = new Set(hiddenSet);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(order, Array.from(next));
  };

  const handleDragStart = (key) => (e) => {
    setDragKey(key);
    e.dataTransfer.effectAllowed = 'move';
    // Required for Firefox to start the drag.
    try { e.dataTransfer.setData('text/plain', key); } catch (_) { /* ignore */ }
  };

  const handleListDragOver = (e) => {
    if (!dragKey) return;
    e.preventDefault();
    updateDragAutoScrollFromClientY(e.clientY);
  };

  const handleDragOver = (overKey) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    updateDragAutoScrollFromClientY(e.clientY);
    if (!dragKey || dragKey === overKey) return;
    const next = [...order];
    const fromIdx = next.indexOf(dragKey);
    const toIdx = next.indexOf(overKey);
    if (fromIdx < 0 || toIdx < 0) return;
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, dragKey);
    onChange(next, hidden);
  };

  const handleDragEnd = () => {
    stopDragAutoScroll();
    setDragKey(null);
  };

  const handleReset = () => {
    onChange(defaults.order, defaults.hidden);
  };

  const popup =
    open && position
      ? createPortal(
          <div
            ref={popupRef}
            className="fixed w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden"
            style={{ top: position.top, left: position.left, width: COLUMN_MENU_WIDTH }}
          >
            <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Columns</span>
              <button
                type="button"
                onClick={handleReset}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                Reset
              </button>
            </div>
            <ul
              ref={listScrollRef}
              className="max-h-72 overflow-y-auto py-1"
              onDragOver={handleListDragOver}
            >
              {order.map((key) => {
                const col = columnsByKey.get(key);
                if (!col) return null;
                const isHidden = hiddenSet.has(key);
                const isDragging = dragKey === key;
                return (
                  <li
                    key={key}
                    draggable
                    onDragStart={handleDragStart(key)}
                    onDragOver={handleDragOver(key)}
                    onDragEnd={handleDragEnd}
                    onDrop={(e) => e.preventDefault()}
                    className={`flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-gray-50 cursor-grab active:cursor-grabbing ${
                      isDragging ? 'opacity-50 bg-blue-50' : ''
                    }`}
                  >
                    <GripVertical className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                    <span className={`flex-1 truncate ${isHidden ? 'text-gray-400' : 'text-gray-800'}`}>
                      {col.label}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleVisibility(key)}
                      disabled={col.alwaysVisible}
                      title={isHidden ? 'Show column' : 'Hide column'}
                      className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      {isHidden ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-blue-600" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Column settings"
        aria-label="Column settings"
        className="inline-flex items-center justify-center rounded p-1.5 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
      >
        <Settings className="h-4 w-4" />
      </button>
      {popup}
    </div>
  );
}

export default ColumnSettingsMenu;
